import { IPerioChart } from '@dataTypes/chartingFeature.types'

export interface ResolveActivePerioChartParams {
  selectedPerioChart: IPerioChart | null
  createPerioChart: IPerioChart
  perioCharts: IPerioChart[]
  showExistingConditions: boolean
  patientId?: string
  /** True after Add chart / reset — use empty create buffer instead of latest history. */
  preferCreatePerioChart?: boolean
}

export const isExplicitPerioHistorySelection = (
  selectedPerioChart: IPerioChart | null,
  createPerioChart: IPerioChart,
): boolean =>
  Boolean(
    selectedPerioChart?.chart_id &&
      selectedPerioChart.chart_id !== createPerioChart.chart_id,
  )

/** History Edit aligned create + selected buffers on the same persisted chart. */
export const isAlignedPersistedPerioEditSession = (
  selectedPerioChart: IPerioChart | null,
  createPerioChart: IPerioChart,
): boolean => {
  const chartId = createPerioChart.chart_id?.trim() ?? ''
  return Boolean(chartId && selectedPerioChart?.chart_id?.trim() === chartId)
}

/** Chart id whose history-row Edit action should be disabled (active edit session only). */
export const getAlignedPerioEditChartId = (
  selectedPerioChart: IPerioChart | null,
  createPerioChart: IPerioChart,
  preferCreatePerioChart = false,
): string => {
  if (preferCreatePerioChart) {
    return ''
  }
  if (
    !isAlignedPersistedPerioEditSession(selectedPerioChart, createPerioChart)
  ) {
    return ''
  }
  return createPerioChart.chart_id?.trim() ?? ''
}

export const getLatestPerioChartForPatient = (
  perioCharts: IPerioChart[],
  patientId: string,
): IPerioChart | null =>
  perioCharts.find((chart) => chart.patient_id === patientId) ?? null

export const resolveActivePerioChart = ({
  selectedPerioChart,
  createPerioChart,
  perioCharts,
  showExistingConditions,
  patientId,
  preferCreatePerioChart = false,
}: ResolveActivePerioChartParams): IPerioChart => {
  if (isExplicitPerioHistorySelection(selectedPerioChart, createPerioChart)) {
    return selectedPerioChart as IPerioChart
  }

  const createChartId = createPerioChart.chart_id?.trim() ?? ''
  const selectedChartId = selectedPerioChart?.chart_id?.trim() ?? ''

  if (
    preferCreatePerioChart ||
    (createChartId &&
      (!selectedPerioChart || selectedChartId === createChartId))
  ) {
    return createPerioChart
  }

  if (showExistingConditions && patientId) {
    const latestChart = getLatestPerioChartForPatient(perioCharts, patientId)
    if (latestChart) {
      return latestChart
    }
  }

  return createPerioChart
}

export const isPersistedPerioChart = (
  chart: IPerioChart,
  createPerioChart: IPerioChart,
): boolean =>
  Boolean(chart.chart_id && chart.chart_id !== createPerioChart.chart_id)
